import {
  type AuthorizationContext,
  type AuthorizationScope,
  COACHING_TRACKS,
  type CoachingTrack,
  FEATURE_KEYS,
  type FeatureKey,
  JOURNEY_TYPES,
  type JourneyType,
  OFFBOARDING_WORKSTREAMS,
  type OffboardingWorkstream,
  PERMISSION_KEYS,
  PERSONAL_GOALS,
  PROGRAM_TYPES,
  type ParticipantAccess,
  type PermissionKey,
  type PersonalGoal,
  type ProgramType,
  ROLE_KEYS,
  type RoleKey,
  SCOPE_TYPES,
  SEVERANCE_WORKFLOW_STATES,
  type ScopeType,
  type SeveranceWorkflowState,
  type WorkstreamScope,
} from '@/types/access-control'

const catalog = <T extends string>(values: readonly T[]) => new Set<string>(values)
const roleCatalog = catalog(ROLE_KEYS)
const permissionCatalog = catalog(PERMISSION_KEYS)
const featureCatalog = catalog(FEATURE_KEYS)
const programCatalog = catalog(PROGRAM_TYPES)
const personalGoalCatalog = catalog(PERSONAL_GOALS)
const journeyCatalog = catalog(JOURNEY_TYPES)
const coachingTrackCatalog = catalog(COACHING_TRACKS)
const workflowCatalog = catalog(SEVERANCE_WORKFLOW_STATES)
const workstreamCatalog = catalog(OFFBOARDING_WORKSTREAMS)
const scopeCatalog = catalog(SCOPE_TYPES)
const platformRoles = new Set<RoleKey>([
  'platform_admin',
  'coach',
  'coach_manager',
  'account_manager',
  'benefits_triage',
])

interface ParsedRecord extends Record<string, unknown> {
  assigned_organization_ids?: unknown
  coaching_track?: unknown
  cohort_features?: unknown
  cohort_id?: unknown
  cooling_off_ends_at?: unknown
  effective_features?: unknown
  expires_at?: unknown
  journey_type?: unknown
  offboarding_workstreams?: unknown
  organization_entitlements?: unknown
  organization_id?: unknown
  participant?: unknown
  program_features?: unknown
  program_type?: unknown
  primary_goal?: unknown
  allowed_goals?: unknown
  permissions?: unknown
  program_id?: unknown
  roles?: unknown
  scope_type?: unknown
  scopes?: unknown
  user_id?: unknown
  workflow_restricted?: unknown
  workflow_state?: unknown
  workstream?: unknown
}

function isRecord(value: unknown): value is ParsedRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

function isNullableIsoDate(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && !Number.isNaN(Date.parse(value)))
}

function parseCatalogArray<T extends string>(value: unknown, values: Set<string>): T[] | null {
  if (
    !Array.isArray(value) ||
    !value.every((item) => typeof item === 'string' && values.has(item))
  ) {
    return null
  }
  if (new Set(value).size !== value.length) return null
  return value as T[]
}

function parseScope(value: unknown): AuthorizationScope | null {
  if (!isRecord(value) || !scopeCatalog.has(String(value.scope_type))) return null
  const scopeType = value.scope_type as ScopeType
  const organizationId = value.organization_id
  if (scopeType === 'platform' && organizationId !== null) return null
  if (scopeType === 'organization' && !isPositiveInteger(organizationId)) return null
  const roles = parseCatalogArray<RoleKey>(value.roles, roleCatalog)
  const permissions = parseCatalogArray<PermissionKey>(value.permissions, permissionCatalog)
  if (!roles || !permissions) return null
  if (roles.some((role) => platformRoles.has(role) !== (scopeType === 'platform'))) return null
  return {
    scope_type: scopeType,
    organization_id: organizationId as number | null,
    roles,
    permissions,
  }
}

function parseParticipant(value: unknown): ParticipantAccess | null | undefined {
  if (value === null) return null
  if (!isRecord(value)) return undefined
  if (
    !isPositiveInteger(value.program_id) ||
    !isPositiveInteger(value.organization_id) ||
    !isPositiveInteger(value.cohort_id) ||
    !journeyCatalog.has(String(value.journey_type)) ||
    (value.coaching_track !== null && !coachingTrackCatalog.has(String(value.coaching_track))) ||
    !workflowCatalog.has(String(value.workflow_state)) ||
    typeof value.workflow_restricted !== 'boolean' ||
    !isNullableIsoDate(value.cooling_off_ends_at)
  ) {
    return undefined
  }
  const organizationEntitlements = parseCatalogArray<FeatureKey>(
    value.organization_entitlements,
    featureCatalog,
  )
  const cohortFeatures = parseCatalogArray<FeatureKey>(value.cohort_features, featureCatalog)
  const effectiveFeatures = parseCatalogArray<FeatureKey>(value.effective_features, featureCatalog)
  if (!organizationEntitlements || !cohortFeatures || !effectiveFeatures) return undefined
  const inferredProgramType: ProgramType =
    value.journey_type === 'internal_mobility'
      ? 'internal_mobility'
      : value.journey_type === 'outplacement'
        ? 'everyone_plus_severance'
        : value.journey_type === 'voluntary_exit'
          ? 'voluntary_exit'
          : 'everyone_plus'
  const programType =
    value.program_type === undefined
      ? inferredProgramType
      : programCatalog.has(String(value.program_type))
        ? (value.program_type as ProgramType)
        : null
  const programFeatures =
    value.program_features === undefined
      ? cohortFeatures
      : parseCatalogArray<FeatureKey>(value.program_features, featureCatalog)
  const allowedGoals =
    value.allowed_goals === undefined
      ? programType === 'internal_mobility'
        ? (['career'] as PersonalGoal[])
        : ([...PERSONAL_GOALS] as PersonalGoal[])
      : parseCatalogArray<PersonalGoal>(value.allowed_goals, personalGoalCatalog)
  const primaryGoal =
    value.primary_goal === undefined || value.primary_goal === null
      ? null
      : personalGoalCatalog.has(String(value.primary_goal))
        ? (value.primary_goal as PersonalGoal)
        : undefined
  if (!programType || !programFeatures || !allowedGoals || primaryGoal === undefined)
    return undefined
  if (primaryGoal !== null && !allowedGoals.includes(primaryGoal)) return undefined
  return {
    program_id: value.program_id,
    organization_id: value.organization_id,
    cohort_id: value.cohort_id,
    journey_type: value.journey_type as JourneyType,
    program_type: programType,
    coaching_track: value.coaching_track as CoachingTrack | null,
    primary_goal: primaryGoal,
    allowed_goals: allowedGoals,
    organization_entitlements: organizationEntitlements,
    cohort_features: cohortFeatures,
    program_features: programFeatures,
    effective_features: effectiveFeatures,
    workflow_state: value.workflow_state as SeveranceWorkflowState,
    workflow_restricted: value.workflow_restricted,
    cooling_off_ends_at: value.cooling_off_ends_at,
  }
}

function parseWorkstream(value: unknown): WorkstreamScope | null {
  if (
    !isRecord(value) ||
    !isPositiveInteger(value.organization_id) ||
    !workstreamCatalog.has(String(value.workstream)) ||
    !isNullableIsoDate(value.expires_at)
  ) {
    return null
  }
  return {
    organization_id: value.organization_id,
    workstream: value.workstream as OffboardingWorkstream,
    expires_at: value.expires_at,
  }
}

export function parseAuthorizationContext(value: unknown): AuthorizationContext | null {
  if (!isRecord(value) || !isPositiveInteger(value.user_id)) return null
  const permissions = parseCatalogArray<PermissionKey>(value.permissions, permissionCatalog)
  if (
    !permissions ||
    !Array.isArray(value.scopes) ||
    !Array.isArray(value.assigned_organization_ids)
  ) {
    return null
  }
  const scopes = value.scopes.map(parseScope)
  const participant = parseParticipant(value.participant)
  const workstreams = Array.isArray(value.offboarding_workstreams)
    ? value.offboarding_workstreams.map(parseWorkstream)
    : []
  if (
    scopes.some((scope) => scope === null) ||
    participant === undefined ||
    workstreams.some((scope) => scope === null) ||
    !value.assigned_organization_ids.every(isPositiveInteger) ||
    new Set(value.assigned_organization_ids).size !== value.assigned_organization_ids.length
  ) {
    return null
  }
  const scopeKeys = scopes.map((scope) => `${scope?.scope_type}:${scope?.organization_id ?? ''}`)
  if (new Set(scopeKeys).size !== scopeKeys.length) return null
  return {
    user_id: value.user_id,
    permissions,
    scopes: scopes as AuthorizationScope[],
    participant,
    assigned_organization_ids: value.assigned_organization_ids,
    offboarding_workstreams: workstreams as WorkstreamScope[],
  }
}

export function hasPlatformPermission(
  context: AuthorizationContext,
  permission: PermissionKey,
): boolean {
  return context.scopes.some(
    (scope) => scope.scope_type === 'platform' && scope.permissions.includes(permission),
  )
}

export function hasOrganizationPermission(
  context: AuthorizationContext,
  permission: PermissionKey,
  organizationId: number,
): boolean {
  return context.scopes.some(
    (scope) =>
      scope.scope_type === 'organization' &&
      scope.organization_id === organizationId &&
      scope.permissions.includes(permission),
  )
}

export function hasRole(
  context: AuthorizationContext,
  role: RoleKey,
  organizationId?: number,
): boolean {
  return context.scopes.some(
    (scope) =>
      scope.roles.includes(role) &&
      (organizationId === undefined || scope.organization_id === organizationId),
  )
}

export function hasParticipantFeature(
  context: AuthorizationContext,
  feature: FeatureKey,
  options: { allowDuringWorkflow?: boolean } = {},
): boolean {
  const participant = context.participant
  if (!participant) return false
  if (!participant.workflow_restricted) return participant.effective_features.includes(feature)
  if (options.allowDuringWorkflow !== true) return false
  return (
    participant.organization_entitlements.includes(feature) &&
    (participant.program_features ?? participant.cohort_features).includes(feature)
  )
}

export function hasAssignedOrganization(
  context: AuthorizationContext,
  organizationId: number,
): boolean {
  return context.assigned_organization_ids.includes(organizationId)
}

export function hasOffboardingWorkstream(
  context: AuthorizationContext,
  organizationId: number,
  workstream: OffboardingWorkstream,
  now = new Date(),
): boolean {
  return context.offboarding_workstreams.some(
    (scope) =>
      scope.organization_id === organizationId &&
      scope.workstream === workstream &&
      (scope.expires_at === null || new Date(scope.expires_at).getTime() > now.getTime()),
  )
}

export function firstAllowedParticipantRoute(context: AuthorizationContext): string | null {
  const participant = context.participant
  if (!participant) return null
  if (participant.workflow_restricted) {
    const canReachElection =
      hasOrganizationPermission(
        context,
        'participant.portal.access',
        participant.organization_id,
      ) && hasParticipantFeature(context, 'benefits', { allowDuringWorkflow: true })
    return canReachElection ? '/benefits/election' : null
  }
  const features = new Set(participant.effective_features)
  if (participant.program_type === 'internal_mobility') {
    // Internal Transfer lands on Home: /resume is guarded by
    // participant.external-jobs, whose programs exclude internal_mobility, so
    // sending an internal-only participant there bounced them straight back
    // here and dead-ended on Access denied (PEL-1030).
    if (features.has('internal_jobs') || features.has('external_jobs')) return '/home'
    if (features.has('skills_map')) return '/career-map'
  }
  if (
    (participant.primary_goal === 'entrepreneurship' ||
      participant.primary_goal === 'retirement') &&
    features.has('coaching')
  ) {
    return '/coaching'
  }
  if (participant.journey_type === 'voluntary_exit' && features.has('benefits')) return '/benefits'
  if (
    (participant.journey_type === 'entrepreneurship' ||
      participant.journey_type === 'retirement') &&
    features.has('coaching')
  ) {
    return '/coaching'
  }
  if (features.has('external_jobs')) return '/resume'
  if (features.has('benefits')) return '/benefits'
  if (features.has('coaching')) return '/coaching'
  if (features.has('skills_map')) return '/career-map'
  if (features.has('learn')) return '/learn'
  return null
}
